import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api } from "./api";
import type { RunEvent } from "./types";

/** Cap the in-memory log so a long run doesn't grow the tab without bound. */
const MAX_EVENTS = 4000;

/**
 * The skill whose call means the manifest's evidence block just changed.
 *
 * Matched by pattern, not by name: skills are namespaced by the *playbook*
 * key, so a finding recorded by the company-profile playbook arrives as
 * `mcp__company-profile__record_finding`. Hard-coding `mcp__cdd__` meant this
 * fired for one playbook out of seven, and the Overview sat still for all the
 * others.
 */
const RECORD_FINDING = /^mcp__.+__record_finding$/;

/** How often the log is re-read when the stream is not carrying it. */
const POLL_MS = 2_500;

export type StreamState = "connecting" | "open" | "closed" | "error";

/**
 * Follow a run's server-sent event stream.
 *
 * The backend replays history before going live, so a page opened mid-run —
 * or reloaded — sees the whole run, not just what happens next. Phase
 * boundaries invalidate the cached manifest so the timeline and stat tiles
 * refresh without polling.
 */
export function useEventStream(runId: string | undefined, enabled = true) {
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [state, setState] = useState<StreamState>("connecting");
  const queryClient = useQueryClient();
  const buffer = useRef<RunEvent[]>([]);
  const flushTimer = useRef<number | null>(null);
  //: How many events have reached this page over the **stream**. Used for
  //: nothing but the log's own bookkeeping — never as a file offset. See
  //: `polled` below for why those had to stop being one number.
  const received = useRef(0);

  //: How far the **poll** has read `events.jsonl`, which is a different
  //: sequence from the one above and was the same variable until a live run
  //: wedged on it.
  //
  //: The stream's count drifts above the file's for two reasons that cannot
  //: be designed away from this side: `subscribe` replays the whole buffer to
  //: every reconnection, and the job publishes events — `run.started`, a queue
  //: position — that are never written to the file at all. Measured on a live
  //: run: four reconnects in an hour, a stream count of 1,731 against 915
  //: events on disk, and `?since=1731` returning an empty page for ever after.
  //:
  //: Only the server can say how far the file goes, so only the server's
  //: `total` ever sets this.
  const polled = useRef(0);

  //: Whether the next poll has to **replace** the log rather than extend it.
  //
  //: Splitting the two counters above costs the property that made one of them
  //: work: the poll used to ask for what came after the stream, so the two
  //: routes could not duplicate each other. They can now — so the first poll
  //: after the stream stops carrying re-reads the file from the start and
  //: replaces what is on screen. The file is the record, which is exactly what
  //: a reload shows, so a resync converges the page on the state a reload
  //: would have given it. Cheap, because it happens only when the stream is
  //: down rather than on every tick.
  const resync = useRef(true);

  useEffect(() => {
    if (!runId || !enabled) {
      setState("closed");
      return;
    }

    setEvents([]);
    buffer.current = [];
    received.current = 0;
    polled.current = 0;
    resync.current = true;
    setState("connecting");

    const source = new EventSource(api.eventsUrl(runId));

    // Batch renders: a research phase can emit events faster than React
    // should re-render a 4000-row log.
    const flush = () => {
      flushTimer.current = null;
      if (buffer.current.length === 0) return;
      const incoming = buffer.current;
      buffer.current = [];
      setEvents((prev) => {
        const next = prev.concat(incoming);
        received.current = next.length;
        return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
      });
    };

    const schedule = () => {
      if (flushTimer.current === null) {
        flushTimer.current = window.setTimeout(flush, 120);
      }
    };

    source.onopen = () => {
      // Anything the poll picked up while the stream was down overlaps with
      // the replay that is about to arrive, so the next poll starts over.
      resync.current = true;
      setState("open");
    };

    source.onmessage = (message) => {
      let event: RunEvent;
      try {
        event = JSON.parse(message.data) as RunEvent;
      } catch {
        return;
      }
      buffer.current.push(event);
      schedule();

      // Anything that changes the run's shape invalidates the manifest.
      //
      // `phase.skip` was missing, and it is the one that shows: both callers
      // of `skip_phase` target `memorise`, the *last* phase, so the row sat on
      // `pending` with no `phase.start` or `phase.end` ever coming — wrong at
      // exactly the moment somebody is watching the run end, and self-righting
      // only when `run.finished` arrived after it (#90).
      //
      // `test_every_phase_event_the_server_emits_invalidates_the_manifest`
      // holds the general case, because the next event type added has the same
      // gap waiting for it.
      if (
        event.type === "phase.start" ||
        event.type === "phase.end" ||
        event.type === "phase.skip" ||
        event.type === "gate.verdict" ||
        event.type === "run.finished" ||
        event.type === "run.error"
      ) {
        queryClient.invalidateQueries({ queryKey: ["run", runId] });
        queryClient.invalidateQueries({ queryKey: ["runs"] });
      }

      // A recorded finding rewrites the manifest too — coverage, confidence
      // mix and the findings count all move. Without this the Overview sat
      // still for the length of a phase (seven minutes in a real run) while
      // the Evidence tab beside it counted up, which reads as a broken page.
      // Matched narrowly: invalidating on every tool result would refetch the
      // manifest a few hundred times a run for no new data.
      if (event.type === "tool.result" && RECORD_FINDING.test(event.tool ?? "")) {
        queryClient.invalidateQueries({ queryKey: ["run", runId] });
      }

      if (event.type === "tool.result" || event.type === "phase.end") {
        queryClient.invalidateQueries({ queryKey: ["findings", runId] });
        queryClient.invalidateQueries({ queryKey: ["sources", runId] });
      }
      if (event.type === "run.finished" || event.type === "run.error") {
        queryClient.invalidateQueries({ queryKey: ["artefacts", runId] });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        // The run has just been settled: its reservation released and the
        // real cost charged. Both the header and the billing page are stale.
        queryClient.invalidateQueries({ queryKey: ["credits"] });
        queryClient.invalidateQueries({ queryKey: ["me"] });
        source.close();
        setState("closed");
      }
    };

    source.addEventListener("end", () => {
      source.close();
      setState("closed");
    });

    source.onerror = () => {
      // EventSource reconnects on its own; only report a hard close.
      if (source.readyState === EventSource.CLOSED) setState("error");
    };

    return () => {
      if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
      source.close();
    };
  }, [runId, enabled, queryClient]);

  // Fallback. EventSource retries on its own, but a stream that is blocked
  // rather than dropped never recovers — a buffering proxy, an extension, a
  // tab the browser suspended. A log that has quietly stopped growing looks
  // exactly like a run that has quietly stopped, so poll whenever the stream
  // is not actually open and merge whatever the page has not seen.
  useEffect(() => {
    if (!runId || !enabled || state === "open") return;

    let cancelled = false;
    const tick = async () => {
      try {
        const from = resync.current ? 0 : polled.current;
        const page = await api.eventLog(runId, from);
        if (cancelled) return;
        // **Before the early return, not after it.** An empty page is the one
        // that heals an offset which has run past the end of the file: ask for
        // events after 1,731 of a 915-event log and the answer is `[]` for
        // ever, and the line that corrects the count used to sit behind
        // `page.events.length === 0`. A live run sat frozen on exactly that,
        // with the header still reporting how many events it held.
        polled.current = page.total;
        if (resync.current) {
          resync.current = false;
          const fresh = page.events.slice(-MAX_EVENTS);
          received.current = fresh.length;
          setEvents(fresh);
          queryClient.invalidateQueries({ queryKey: ["run", runId] });
          return;
        }
        if (page.events.length === 0) return;
        setEvents((prev) => {
          const next = prev.concat(page.events);
          received.current = next.length;
          return next.length > MAX_EVENTS
            ? next.slice(next.length - MAX_EVENTS)
            : next;
        });
        queryClient.invalidateQueries({ queryKey: ["run", runId] });
      } catch {
        // The run may have been removed, or the API is briefly unreachable.
        // The next tick will find out; there is nothing useful to say here.
      }
    };

    void tick();
    const timer = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [runId, enabled, state, queryClient]);

  return { events, state };
}
